import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, validating, valid, invalid, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setErrorMessage('No reset token provided');
    } else {
      // Optionally validate token on load
      setStatus('valid');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to reset password. The link may be expired.');
    }
  };

  return (
    <div className="reset-password-page" data-testid="reset-password-page">
      <div className="reset-container">
        <motion.div
          className="reset-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {status === 'validating' && (
            <div className="status-state">
              <div className="status-icon validating">
                <Loader2 size={48} className="spinning" />
              </div>
              <h1>Validating Link</h1>
              <p>Please wait while we verify your reset link...</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="status-state">
              <div className="status-icon error">
                <XCircle size={48} />
              </div>
              <h1>Invalid Link</h1>
              <p>{errorMessage || 'This password reset link is invalid or has expired.'}</p>
              <Link to="/forgot-password" className="btn btn-primary">
                Request New Link
              </Link>
            </div>
          )}

          {status === 'success' && (
            <div className="status-state">
              <div className="status-icon success">
                <CheckCircle size={48} />
              </div>
              <h1>Password Reset!</h1>
              <p>Your password has been successfully reset. You can now sign in with your new password.</p>
              <Link to="/" className="btn btn-primary" data-testid="go-login-btn">
                Sign In <ArrowRight size={18} />
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="status-state">
              <div className="status-icon error">
                <XCircle size={48} />
              </div>
              <h1>Reset Failed</h1>
              <p>{errorMessage}</p>
              <Link to="/forgot-password" className="btn btn-primary">
                Try Again
              </Link>
            </div>
          )}

          {(status === 'valid' || status === 'submitting') && (
            <>
              <div className="reset-header">
                <div className="reset-icon">
                  <Lock size={32} />
                </div>
                <h1>Reset Password</h1>
                <p>Enter your new password below.</p>
              </div>

              <form onSubmit={handleSubmit} className="reset-form" data-testid="reset-password-form">
                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                {errorMessage && status !== 'error' && (
                  <div className="error-message" data-testid="reset-error">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={status === 'submitting'}
                  data-testid="reset-submit-btn"
                >
                  {status === 'submitting' ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>

      <style>{`
        .reset-password-page {
          min-height: calc(100vh - 200px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
        }

        .reset-container {
          width: 100%;
          max-width: 440px;
        }

        .reset-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
        }

        .reset-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .reset-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .reset-header h1 {
          font-size: 1.75rem;
          margin-bottom: 8px;
        }

        .reset-header p {
          color: var(--text-muted);
          font-size: 14px;
        }

        .reset-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .form-group input {
          padding: 12px 16px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 16px;
        }

        .form-group input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .password-input {
          position: relative;
        }

        .password-input input {
          width: 100%;
          padding-right: 48px;
        }

        .toggle-password {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 4px;
        }

        .toggle-password:hover {
          color: var(--text-primary);
        }

        .error-message {
          padding: 12px;
          background: var(--error-bg);
          color: var(--error);
          border-radius: 8px;
          font-size: 14px;
        }

        .btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--primary);
          color: #0a0a0f;
          border: none;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--primary-light);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Status States */
        .status-state {
          text-align: center;
          padding: 20px 0;
        }

        .status-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .status-icon.validating {
          background: rgba(100, 100, 255, 0.1);
          color: #8888ff;
        }

        .status-icon.success {
          background: rgba(0, 255, 148, 0.1);
          color: var(--success);
        }

        .status-icon.error {
          background: rgba(255, 59, 48, 0.1);
          color: var(--error);
        }

        .status-state h1 {
          font-size: 1.75rem;
          margin-bottom: 12px;
        }

        .status-state p {
          color: var(--text-muted);
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;
